import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SirenIcon } from "lucide-react";
import { toast } from "sonner";
import { ALERT_KIND_LABELS } from "@brewbase/lib";
import type { FermentationAlertId } from "@brewbase/db/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trpc } from "@/hooks/trpc";

/** How often the browser asks whether anything is waiting for this operator. */
const POLL_MS = 30_000;

/**
 * The alert that interrupts whatever you were doing.
 *
 * Shown on every page, to the people on call for the group it belongs to, and
 * not dismissible without acknowledging: a raised alert that someone can click
 * away is a raised alert nobody owns. Acknowledging is not fixing — it stops
 * the escalation to a phone call and records who is dealing with it.
 *
 * Deliberately separate from `GlobalAlertDialog`, which is the confirm/prompt
 * helper and has nothing to do with process alerting despite the name.
 */
export function ActiveAlertDialog() {
  const utils = trpc.useUtils();
  const [acking, setAcking] = useState<FermentationAlertId | null>(null);

  const { data: alerts } = trpc.fermentationAlerts.mine.useQuery(undefined, {
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    // A failed poll must not leave a stale "all clear" on screen for long.
    retry: true,
  });

  const acknowledge = trpc.fermentationAlerts.acknowledge.useMutation({
    onSuccess: () => {
      setAcking(null);
      void utils.fermentationAlerts.mine.invalidate();
    },
    onError: (error) => {
      setAcking(null);
      toast.error(error.message);
    },
  });

  const alert = alerts?.[0];
  if (!alert) return null;

  const more = (alerts?.length ?? 0) - 1;

  return (
    <Dialog open>
      <DialogContent
        className="max-w-lg"
        // No close button and no dismissal: the only way out is to take it.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-tomato-11">
            <SirenIcon className="w-4 h-4" />
            {ALERT_KIND_LABELS[alert.kind]}
          </DialogTitle>
          <DialogDescription>
            {alert.group_name}
            {alert.fermentation_name ? ` · ${alert.fermentation_name}` : ""} —
            raised {new Date(alert.raised_at).toLocaleTimeString()}
          </DialogDescription>
        </DialogHeader>

        <p className="text-base">{alert.detail}</p>

        {more > 0 && (
          <p className="text-sm text-muted-foreground">
            {more} other alert{more === 1 ? "" : "s"} waiting.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" asChild>
            <Link
              to="/fermentations/$group_id/planning/alerts"
              params={{ group_id: alert.fermentation_group_id }}
            >
              Open the run
            </Link>
          </Button>
          <Button
            disabled={acknowledge.isPending}
            onClick={() => {
              setAcking(alert.id);
              acknowledge.mutate(alert.id);
            }}
          >
            {acking === alert.id ? "Acknowledging…" : "Acknowledge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
